import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  FlexAdminOrderDetail,
  FlexAdminOrderRow,
  FlexCarrierPayment,
  FlexOrderUpdatePatch,
  FlexPlace,
  FlexStatus,
} from "@mnp/shared";
import { api } from "./api";

export type { FlexCarrierPayment, FlexOrderUpdatePatch, FlexPlace };

// Wire-типы админ-эндпоинта /admin/flex-orders живут в @mnp/shared
// (packages/shared/src/flex/wire.ts, карточка flex-wire-contract-shared) —
// здесь только алиасы под исторические имена хука. Серверная сторона связана
// тем же типом key-alignment контрактом flex.wire.contract.test.ts в api.
export type FlexOrderRow = FlexAdminOrderRow;
export type FlexOrderDetail = FlexAdminOrderDetail;

export interface FlexOrderAttachment {
  id: string;
  type: "photo" | "note";
  description?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  createdAt: string;
}

export interface FlexOrdersPage {
  rows: FlexOrderRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface FlexOrdersQuery {
  status?: FlexStatus;
  chatId?: string | number;
  page?: number;
  pageSize?: number;
  /** When false the query is not fired (used to keep the default tab cheap). */
  enabled?: boolean;
}

function buildFlexQuery(query: FlexOrdersQuery): string {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.chatId !== undefined) params.set("chatId", String(query.chatId));
  if (query.page) params.set("page", String(query.page));
  if (query.pageSize) params.set("pageSize", String(query.pageSize));
  const q = params.toString();
  return q ? `?${q}` : "";
}

export const FLEX_ORDERS_QUERY_KEY = "admin-flex-orders";
export const FLEX_ORDER_QUERY_KEY = "admin-flex-order";
export const FLEX_PICKUPS_QUERY_KEY = "admin-flex-pickups";

export function useFlexOrders(query: FlexOrdersQuery = {}) {
  const { enabled = true, ...rest } = query;
  return useQuery({
    queryKey: [FLEX_ORDERS_QUERY_KEY, rest],
    queryFn: () => api<FlexOrdersPage>(`/admin/flex-orders${buildFlexQuery(rest)}`),
    enabled,
  });
}

export function useFlexOrder(id: string | null) {
  return useQuery({
    queryKey: [FLEX_ORDER_QUERY_KEY, id],
    queryFn: () => api<FlexOrderDetail>(`/admin/flex-orders/${id}`),
    enabled: !!id,
  });
}

export const FLEX_ORDER_ATTACHMENTS_QUERY_KEY = "admin-flex-order-attachments";

export function useFlexOrderAttachments(id: string | null) {
  return useQuery({
    queryKey: [FLEX_ORDER_ATTACHMENTS_QUERY_KEY, id],
    queryFn: () =>
      api<FlexOrderAttachment[]>(`/admin/flex-orders/${id}/attachments`),
    enabled: !!id,
  });
}

// Patch admin-editable fields (weight, dimensions, customs risk, processing
// addresses, contents summary) AND every user-submitted field — the admin can
// correct any item of a filled order. Тип FlexOrderUpdatePatch — z.input
// серверной updateFlexOrderSchema из @mnp/shared (реэкспорт в шапке файла):
// рукописного зеркала больше нет, дрейф схемы ловится компилятором.
export function useFlexOrderUpdate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: FlexOrderUpdatePatch) =>
      api(`/admin/flex-orders/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FLEX_ORDER_QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: [FLEX_ORDERS_QUERY_KEY] });
    },
  });
}

// Забор (2026-06-05): назначить/изменить перевозчика и редактируемую дату
// забора. Узкий PATCH рядом с общим update — сервер валидирует selfDeliver и
// стадию (после approve). pickupDate уходит ISO «YYYY-MM-DD».
export interface FlexCarrierPatch {
  pickupCarrierRef?: string | null;
  pickupDate?: string | null;
  carrierFeeGel?: number | null;
}

export function useFlexCarrier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FlexCarrierPatch) =>
      api(`/admin/flex-orders/${id}/carrier`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FLEX_ORDER_QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: [FLEX_ORDERS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [FLEX_PICKUPS_QUERY_KEY] });
    },
  });
}

// Забор · оплата перевозчику (2026-06-05, Phase B): согласованная сумма услуг
// (carrierFeeGel) + её распределение по способам (наличные/перевод, можно
// остаться должны). Полный список строк уходит на сервер целиком (PUT).
export interface FlexCarrierPaymentsPatch {
  carrierFeeGel?: number | null;
  payments: Array<{
    id?: string;
    method: "cash" | "transfer";
    amountGel: number;
    comment?: string | null;
    at?: string;
  }>;
}

export function useFlexCarrierPayments(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FlexCarrierPaymentsPatch) =>
      api(`/admin/flex-orders/${id}/carrier-payments`, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FLEX_ORDER_QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: [FLEX_ORDERS_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [FLEX_PICKUPS_QUERY_KEY] });
    },
  });
}

// Забор · лента «Заборы» (2026-06-05, Phase C): курьерские заборы, ещё не
// доехавшие до склада (approved/pickup), отсортированы по дате забора —
// ближайшие сверху. Поля урезаны под список (без PII-паспорта).
export interface FlexPickupRow {
  id: string;
  status: FlexStatus;
  requestNumber: string | null;
  chatId: string;
  pickupDate: string | null;
  pickupDay: string | null;
  pickupSlot: string | null;
  pickupCarrierRef: string | null;
  pickupAddress: string | null;
  senderFullName: string | null;
  originCountry: string | null;
  destinationCountry: string | null;
  carrierFeeGel: string | null;
  createdAt: string;
}

export function useFlexPickups(enabled = true) {
  return useQuery({
    queryKey: [FLEX_PICKUPS_QUERY_KEY],
    queryFn: () => api<{ rows: FlexPickupRow[] }>("/admin/flex-orders/pickups"),
    enabled,
  });
}

export function useFlexAnnounceTotal(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { amountEur?: number; amountGel: number; kind: "preliminary" | "final" }) =>
      api(`/admin/flex-orders/${id}/announce-total`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FLEX_ORDER_QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: [FLEX_ORDERS_QUERY_KEY] });
    },
  });
}

// Ответ POST /admin/flex-orders/:id/payment (recordFlexPayment).
// alreadyRecorded: true — сервер НИЧЕГО не записал: платёж с этим ключом уже
// был учтён ранее (ретрай). UI обязан показать оператору предупреждение,
// а не тихий успех (см. FlexBody).
export interface FlexPaymentResult {
  newTotalGel: number;
  finalAmountGel: number | null;
  fullyPaid: boolean;
  method: "cash" | "card" | "transfer";
  cashboxOperationId: string | null;
  alreadyRecorded?: true;
}

// Приём оплаты по заявке (частичной/полной) из карточки. Копит сумму на сервере,
// пишет кассовую операцию, проставляет «оплачено» при закрытии итога.
// idempotencyKey (uuid, генерируется при открытии формы) защищает от
// задвоения при ретрае/даблклике: повтор с тем же ключом и суммой — no-op на
// сервере (alreadyRecorded), тот же ключ с другой суммой → 409 idempotency_mismatch.
export function useFlexPayment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      amountGel: number;
      method: "cash" | "card" | "transfer";
      idempotencyKey?: string;
    }) =>
      api<FlexPaymentResult>(`/admin/flex-orders/${id}/payment`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FLEX_ORDER_QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: [FLEX_ORDERS_QUERY_KEY] });
    },
  });
}

// Auto-quote (Личные вещи). Two blocks: preliminary (from the manual «Предв.
// вес») and final (from Σ max(actual, volumetric) over places). A block's
// ok=false carries a reason ("commercial" / "serbia_other" / "unknown_country"
// / "no_weight") so the UI shows «уточнить».
export interface FlexQuoteBlock {
  ok: boolean;
  reason?: string;
  eurTotal?: number;
  gelExtra?: number;
  gelExact?: number | null;
  gelFinal?: number | null;
  weightKg?: number | null;
  weightBilledKg?: number;
  lines?: Array<{ label: string; amount: number; currency: "EUR" | "GEL" }>;
}
export interface FlexQuote {
  rate?: number | null;
  finalWeightKg?: number | null;
  preliminary: FlexQuoteBlock;
  final: FlexQuoteBlock;
}

export function useFlexQuote(
  id: string | null,
  opts: { updatedAt?: string | null; enabled?: boolean } = {},
) {
  // Keyed on updatedAt so the quote refetches after any save (weight, places,
  // category — all bump updatedAt).
  return useQuery({
    queryKey: ["admin-flex-quote", id, opts.updatedAt ?? null],
    queryFn: () => api<FlexQuote>(`/admin/flex-orders/${id}/quote`),
    enabled: !!id && (opts.enabled ?? true),
  });
}

export function useFlexStatusTransition(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (newStatus: FlexStatus) =>
      api(`/admin/flex-orders/${id}/status`, {
        method: "POST",
        body: JSON.stringify({ newStatus }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FLEX_ORDER_QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: [FLEX_ORDERS_QUERY_KEY] });
    },
  });
}

// Review-цикл (2026-05-29). approve и reject — отдельные эндпоинты, потому
// что reject требует свободный текст причины и шлёт ТГ-сообщение юзеру.
export function useFlexApprove(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api(`/admin/flex-orders/${id}/approve`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FLEX_ORDER_QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: [FLEX_ORDERS_QUERY_KEY] });
    },
  });
}

export function useFlexReject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api(`/admin/flex-orders/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FLEX_ORDER_QUERY_KEY, id] });
      qc.invalidateQueries({ queryKey: [FLEX_ORDERS_QUERY_KEY] });
    },
  });
}
